import { useEffect, useMemo, useState } from "react";
import { router } from "@inertiajs/react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ArrowUpRight,
  TrendingUp,
  TrendingDown,
  Minus,
  X,
  Download,
} from "lucide-react";
import { ImpactDashboardTemplate } from "@/portal/templates/ImpactDashboardTemplate";
import { AmberEyebrow } from "@/portal/components/AmberEyebrow";
import { Button } from "@/components/Button";
import { IconButton } from "@/components/IconButton";
import { cn } from "@/lib/cn";
import {
  type DayAggregate,
  type MonthSummary,
  DAY_LETTERS,
  formatMoney,
  formatMoneyCompact,
  chunkWeeks,
  maxDayTotal,
} from "./calendar";

interface Props {
  year: number;
  month: number; // 1–12
  monthLabel: string;
  /** Server "today" as ISO yyyy-mm-dd. */
  today: string;
  /** Visible grid range (Monday-first, full weeks), in order. */
  days: DayAggregate[];
  summary: MonthSummary;
}

export default function CalendarIndex({ year, month, monthLabel, today, days, summary }: Props) {
  const [selectedIso, setSelectedIso] = useState<string | null>(today);
  const [panelExpanded, setPanelExpanded] = useState(false);

  const weeks = useMemo(() => chunkWeeks(days), [days]);
  const byDate = useMemo(() => {
    const m = new Map<string, DayAggregate>();
    days.forEach((d) => m.set(d.date, d));
    return m;
  }, [days]);
  const heatMax = useMemo(
    () => maxDayTotal(days.filter((d) => Number(d.date.slice(5, 7)) === month)),
    [days, month],
  );
  const selected = selectedIso ? byDate.get(selectedIso) ?? null : null;

  function navigateMonth(y: number, m: number) {
    router.get("/calendar", { year: y, month: m }, { preserveScroll: true });
  }
  function stepMonth(delta: number) {
    const next = new Date(year, month - 1 + delta, 1);
    navigateMonth(next.getFullYear(), next.getMonth() + 1);
  }
  function goToday() {
    const [ty, tm] = today.split("-").map(Number) as [number, number];
    navigateMonth(ty, tm);
  }
  function onSelectDay(iso: string) {
    if (iso === selectedIso) {
      setSelectedIso(null);
      setPanelExpanded(false);
    } else {
      setSelectedIso(iso);
    }
  }

  const trend: "up" | "down" | "flat" =
    summary.deltaPct > 0.05 ? "up" : summary.deltaPct < -0.05 ? "down" : "flat";
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;

  const monthStart = `${year}-${String(month).padStart(2, "0")}-01`;
  const monthEnd = `${year}-${String(month).padStart(2, "0")}-${String(
    new Date(year, month, 0).getDate(),
  ).padStart(2, "0")}`;
  const exportHref = `/contributions.csv?start_date=${monthStart}&end_date=${monthEnd}`;

  return (
    <ImpactDashboardTemplate
      title="Calendar"
      subtitle="Month grid of contribution activity. Click any day to see what happened."
      breadcrumbs={[{ label: "Home", to: "/" }, { label: "Calendar" }]}
      hideTimeRange
      actions={
        <Button asChild intent="secondary">
          <a href={exportHref}>
            <Download /> Export month
          </a>
        </Button>
      }
    >
      {/* ── Month header ────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <AmberEyebrow tone="atlantic">
            {trend === "flat"
              ? "—"
              : `${summary.deltaPct > 0 ? "+" : ""}${summary.deltaPct.toFixed(1)}% vs prev month`}
          </AmberEyebrow>
          <h2 className="font-brand text-2xl font-semibold tracking-tight text-fg">{monthLabel}</h2>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
            <span className="tabular">
              <span className="font-medium text-fg">{summary.count.toLocaleString()}</span> contributions
            </span>
            <span className="text-fg-subtle">·</span>
            <span className="tabular">
              <span className="font-medium text-fg">{formatMoneyCompact(summary.totalMinor)}</span> allocated
            </span>
            <span
              className={cn(
                "inline-flex items-center gap-0.5 tabular [&_svg]:size-3",
                trend === "up" && "text-success-fg",
                trend === "down" && "text-danger-fg",
                trend === "flat" && "text-fg-subtle",
              )}
            >
              <TrendIcon />
              {Math.abs(summary.deltaPct).toFixed(1)}%
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <Button intent="ghost" size="sm" onClick={goToday}>
            Today
          </Button>
          <span className="mx-1 h-5 w-px bg-border-subtle" aria-hidden="true" />
          <IconButton aria-label="Previous month" onClick={() => stepMonth(-1)} size="sm">
            <ChevronLeft />
          </IconButton>
          <IconButton aria-label="Next month" onClick={() => stepMonth(1)} size="sm">
            <ChevronRight />
          </IconButton>
        </div>
      </div>

      {/* ── Grid ────────────────────────────────────────────────── */}
      <div className="mt-6 overflow-hidden rounded-[var(--radius-lg)] border border-border-subtle bg-surface">
        <div className="grid grid-cols-7 border-b border-border-subtle bg-subtle">
          {DAY_LETTERS.map((d, i) => (
            <div
              key={d}
              className={cn(
                "px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-fg-subtle",
                (i === 5 || i === 6) && "text-fg-disabled",
              )}
            >
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {weeks.flat().map((cell) => (
            <DayCell
              key={cell.date}
              cell={cell}
              currentMonth={month}
              today={today}
              selectedIso={selectedIso}
              onSelect={onSelectDay}
              heatMax={heatMax}
            />
          ))}
        </div>

        <div className="flex items-center justify-between border-t border-border-subtle bg-subtle px-3 py-2 text-[11px] text-fg-subtle">
          <div className="flex items-center gap-2">
            <span>Less</span>
            {[0.08, 0.22, 0.45, 0.7, 1].map((op, i) => (
              <span
                key={i}
                aria-hidden="true"
                className="size-3 rounded-[3px]"
                style={{
                  background: i === 0 ? "var(--bg-muted)" : "var(--brand-atlantic)",
                  opacity: i === 0 ? 1 : op,
                }}
              />
            ))}
            <span>More</span>
          </div>
          <span className="tabular">Peak day: {formatMoneyCompact(heatMax)}</span>
        </div>
      </div>

      {/* Spacer so the floating panel doesn't cover the legend when open. */}
      {selected && <div aria-hidden="true" className="h-44" />}

      <DayBottomPanel
        day={selected}
        today={today}
        expanded={panelExpanded}
        onToggleExpanded={() => setPanelExpanded((e) => !e)}
        onClose={() => {
          setSelectedIso(null);
          setPanelExpanded(false);
        }}
      />
    </ImpactDashboardTemplate>
  );
}

function DayCell({
  cell,
  currentMonth,
  today,
  selectedIso,
  onSelect,
  heatMax,
}: {
  cell: DayAggregate;
  currentMonth: number;
  today: string;
  selectedIso: string | null;
  onSelect: (iso: string) => void;
  heatMax: number;
}) {
  const day = Number(cell.date.slice(8, 10));
  const isThisMonth = Number(cell.date.slice(5, 7)) === currentMonth;
  const isToday = cell.date === today;
  const isSelected = cell.date === selectedIso;
  const isFuture = cell.date > today;

  const intensity = heatMax > 0 ? cell.totalMinor / heatMax : 0;
  const opacity =
    intensity <= 0 ? 0 : intensity < 0.2 ? 0.16 : intensity < 0.4 ? 0.3 : intensity < 0.7 ? 0.55 : 0.85;

  return (
    <button
      type="button"
      onClick={() => !isFuture && onSelect(cell.date)}
      disabled={isFuture}
      className={cn(
        "relative h-24 border-b border-r border-border-subtle p-2 text-left transition-[background-color]",
        "[&:nth-child(7n)]:border-r-0",
        !isFuture && "hover:bg-surface-hover",
        isFuture && "cursor-default",
      )}
    >
      {isThisMonth && opacity > 0 && (
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: "var(--brand-atlantic)", opacity }}
        />
      )}
      {isSelected && (
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none border-2"
          style={{ borderColor: "var(--brand-atlantic)" }}
        />
      )}
      {isToday && !isSelected && (
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none border-2"
          style={{ borderColor: "var(--brand-amber)" }}
        />
      )}

      <div className="relative flex h-full flex-col justify-between">
        <div className="flex items-center justify-between">
          <span
            className={cn(
              "text-sm tabular",
              isThisMonth ? (intensity > 0.5 ? "font-medium text-fg" : "text-fg") : "text-fg-disabled",
              isToday && "font-semibold",
            )}
          >
            {day}
          </span>
          {isToday && (
            <span
              aria-hidden="true"
              className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: "var(--brand-amber)" }}
            >
              Today
            </span>
          )}
        </div>

        {isThisMonth && cell.totalMinor > 0 && (
          <div className={cn("text-xs tabular", intensity > 0.5 ? "font-medium text-fg" : "text-fg-muted")}>
            {formatMoneyCompact(cell.totalMinor)}
            <div className="text-[11px] text-fg-subtle">{cell.count.toLocaleString()}</div>
          </div>
        )}
        {isThisMonth && cell.totalMinor === 0 && !isFuture && (
          <span className="text-[11px] text-fg-disabled">—</span>
        )}
      </div>
    </button>
  );
}

function DayBottomPanel({
  day,
  today,
  expanded,
  onToggleExpanded,
  onClose,
}: {
  day: DayAggregate | null;
  today: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  onClose: () => void;
}) {
  const [renderedDay, setRenderedDay] = useState<DayAggregate | null>(day);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (day) {
      setRenderedDay(day);
      setClosing(false);
    } else if (renderedDay) {
      setClosing(true);
      const t = window.setTimeout(() => {
        setRenderedDay(null);
        setClosing(false);
      }, 280);
      return () => window.clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  useEffect(() => {
    if (!day) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [day, onClose]);

  if (!renderedDay) return null;

  return (
    <div
      aria-hidden={!day}
      className={cn(
        "fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 pb-3 pointer-events-none",
        "transition-[opacity,transform] duration-[280ms] ease-[var(--ease-out)]",
        closing ? "translate-y-full opacity-0" : "translate-y-0 opacity-100",
      )}
    >
      <div
        role="dialog"
        aria-label={`Details for ${renderedDay.date}`}
        className="pointer-events-auto w-full max-w-3xl rounded-[var(--radius-xl)] border border-border-subtle bg-surface shadow-[var(--shadow-4)]"
      >
        <DayPanelHeader
          day={renderedDay}
          expanded={expanded}
          onToggleExpanded={onToggleExpanded}
          onClose={onClose}
        />
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-[var(--duration-default)] ease-[var(--ease-out)]",
            expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
          )}
          aria-hidden={!expanded}
        >
          <div className="min-h-0 overflow-hidden">
            <DayPanelDetails day={renderedDay} today={today} />
          </div>
        </div>
      </div>
    </div>
  );
}

function DayPanelHeader({
  day,
  expanded,
  onToggleExpanded,
  onClose,
}: {
  day: DayAggregate;
  expanded: boolean;
  onToggleExpanded: () => void;
  onClose: () => void;
}) {
  const [y, m, d] = day.date.split("-").map(Number);
  const longDate = new Date(Date.UTC(y!, (m ?? 1) - 1, d!)).toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const topProject = day.topProjects[0];

  return (
    <div className="relative">
      <div className="flex justify-center pt-2">
        <span aria-hidden="true" className="h-1 w-10 rounded-full bg-border-default" />
      </div>

      <button
        type="button"
        onClick={onToggleExpanded}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse panel" : "Expand panel"}
        className="flex w-full items-center gap-4 px-5 pb-3 pt-2 text-left transition-[background-color] hover:bg-surface-hover rounded-t-[var(--radius-xl)]"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 text-xs">
            <AmberEyebrow tone="atlantic">{longDate}</AmberEyebrow>
            {day.totalMinor === 0 && <span className="text-fg-subtle">No contributions</span>}
          </div>
          {day.totalMinor > 0 && (
            <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className="font-brand text-xl font-semibold tracking-tight text-fg tabular">
                {formatMoney(day.totalMinor)}
              </span>
              <span className="text-xs text-fg-muted tabular">
                {day.count.toLocaleString()} contributions
              </span>
              {topProject && (
                <>
                  <span className="text-fg-subtle">·</span>
                  <span className="truncate text-xs text-fg-muted">
                    Top: <span className="text-fg">{topProject.name}</span>
                  </span>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <span className="hidden sm:inline text-xs font-medium text-fg-muted">
            {expanded ? "Hide details" : "Show details"}
          </span>
          <span
            aria-hidden="true"
            className="flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-fg-muted"
          >
            {expanded ? <ChevronDown className="size-4" /> : <ChevronUp className="size-4" />}
          </span>
        </div>
      </button>

      <IconButton aria-label="Close day details" size="sm" onClick={onClose} className="absolute right-2 top-2">
        <X />
      </IconButton>
    </div>
  );
}

function DayPanelDetails({ day, today }: { day: DayAggregate; today: string }) {
  if (day.totalMinor === 0) {
    return (
      <div className="border-t border-border-subtle px-5 py-4">
        <p className="text-sm leading-relaxed text-fg-muted" style={{ fontFamily: "var(--font-editorial)" }}>
          {day.date > today
            ? "This day is in the future — nothing has been allocated to your projects yet."
            : "Nothing was allocated to your projects on this day. Pick another day in the grid."}
        </p>
      </div>
    );
  }

  const others = Math.max(0, day.projectCount - 1);
  const ledgerHref = `/contributions?start_date=${day.date}&end_date=${day.date}`;

  return (
    <div className="grid gap-px border-t border-border-subtle bg-border-subtle md:grid-cols-2">
      <section className="bg-surface px-5 py-4">
        <div className="text-[11px] font-medium uppercase tracking-wider text-fg-subtle">Top projects</div>
        <ul className="mt-3 flex flex-col gap-3">
          {day.topProjects.map((s) => (
            <li key={s.name}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="truncate text-fg">{s.name}</span>
                <span className="shrink-0 tabular text-fg-muted">{Math.round(s.share * 100)}%</span>
              </div>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full"
                  style={{ width: `${Math.round(s.share * 100)}%`, background: "var(--brand-atlantic)" }}
                />
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-surface px-5 py-4">
        <div className="text-[11px] font-medium uppercase tracking-wider text-fg-subtle">What happened</div>
        <p className="mt-2 text-sm leading-relaxed text-fg-muted" style={{ fontFamily: "var(--font-editorial)" }}>
          {day.count.toLocaleString()} contributions came in
          {day.topProjects[0] && (
            <>
              {" "}— the bulk to <span className="font-medium text-fg">{day.topProjects[0].name}</span>
            </>
          )}
          {others > 0 ? (
            <>, with the remainder across {others} other {others === 1 ? "project" : "projects"}.</>
          ) : (
            <>.</>
          )}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={ledgerHref}
            className="inline-flex items-center gap-1 rounded-full border border-border-subtle bg-canvas px-2.5 py-1 text-xs text-fg-muted transition-colors hover:border-border-default hover:text-fg [&_svg]:size-3"
          >
            View contributions <ArrowUpRight />
          </a>
        </div>
      </section>
    </div>
  );
}
